import apiEndpoints from '../../../apiEndpoints';
import { fetcher } from '../../utils/fetchers/fetcher';
import { ReportTemplate } from '../../../pages/DatasetPrint/types';
import { X_SESSION_TOKEN } from '../../../constants';

/**
 * Stored report templates (AIR-279). The designer edits these; the generated
 * system default is produced by buildDefaultReportTemplate and only reaches the
 * database when the user opens the designer for the first time.
 */

export interface StoredReportTemplateSummary {
  id: string;
  code: string;
  datasetId: string;
  name: string;
  description: string | null;
  isActive: boolean;
  updatedAt: string;
  revision: string;
}

export interface StoredReportTemplateDetail extends StoredReportTemplateSummary {
  layout: ReportTemplate;
  createdAt: string;
}

const base = (): string =>
  `${apiEndpoints.basePath}/webManager/1.0/report-designer/report-templates`;

export const requestReportTemplates = (
  datasetId: string,
  token: string | null,
  forceHeader?: string
): Promise<{ items: StoredReportTemplateSummary[] }> =>
  fetcher<{ items: StoredReportTemplateSummary[] }>(
    base(),
    'GET',
    token,
    forceHeader,
    { datasetId }
  );

export const requestReportTemplate = (
  templateId: string,
  token: string | null,
  forceHeader?: string
): Promise<StoredReportTemplateDetail> =>
  fetcher<StoredReportTemplateDetail>(
    `${base()}/${encodeURIComponent(templateId)}`,
    'GET',
    token,
    forceHeader
  );

/**
 * Creates the row the designer will edit. Called with the generated default, so
 * the default layout is authored in exactly one place (the renderer's
 * generator) and the designer never has to reimplement it.
 */
export const createReportTemplate = (
  body: { datasetId: string; name: string; layout: ReportTemplate },
  token: string | null,
  forceHeader?: string
): Promise<StoredReportTemplateDetail> =>
  fetcher<StoredReportTemplateDetail>(
    base(),
    'POST',
    token,
    forceHeader,
    undefined,
    body as never
  );

export const updateReportTemplate = (
  templateId: string,
  body: {
    expectedRevision: string;
    name?: string;
    layout?: ReportTemplate;
    isActive?: boolean;
  },
  token: string | null,
  forceHeader?: string
): Promise<StoredReportTemplateDetail> =>
  fetcher<StoredReportTemplateDetail>(
    `${base()}/${encodeURIComponent(templateId)}`,
    'PATCH',
    token,
    forceHeader,
    undefined,
    body as never
  );

/** Binary content needs a real Blob; the JSON-oriented Capacitor fetcher cannot
 * preserve it. Authentication stays in the session header, never in an image
 * URL that could leak into browser history or server access logs. */
export const requestReportAssetContent = async (
  assetId: string,
  token: string | null
): Promise<Blob> => {
  const response = await fetch(
    `${apiEndpoints.basePath}/webManager/1.0/report-designer/report-assets/${encodeURIComponent(assetId)}/content`,
    { headers: token ? { [X_SESSION_TOKEN]: token } : undefined }
  );
  if (!response.ok) {
    throw new Error(`Report asset request failed with HTTP ${response.status}`);
  }
  return response.blob();
};
